import React from "react";
import {
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LeadDispatchLog, LeadDispatchStatus } from "../../types";
import { isSensitiveHeader } from "../../services/shared";

const STATUS_CONFIG: Record<
  LeadDispatchStatus,
  { label: string; color: "success" | "warning" | "default" | "error" | "info" }
> = {
  dispatched: { label: "Inviato", color: "success" },
  duplicate: { label: "Duplicato", color: "warning" },
  skipped: { label: "Saltato", color: "default" },
  error: { label: "Errore", color: "warning" },
  failed: { label: "Fallito", color: "error" },
};

interface Props {
  log: LeadDispatchLog | null;
  onClose: () => void;
}

export default function LeadDispatchLogDetail({ log, onClose }: Props) {
  return (
    <Drawer
      anchor="right"
      open={!!log}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 540 } } }}
    >
      {log && <Content log={log} onClose={onClose} />}
    </Drawer>
  );
}

function Content({ log, onClose }: { log: LeadDispatchLog; onClose: () => void }) {
  const status = STATUS_CONFIG[log.status];
  return (
    <Box
      sx={{
        p: 3,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            label={status?.label ?? log.status}
            size="small"
            color={status?.color ?? "default"}
            variant="outlined"
          />
          <Typography variant="caption" color="text.secondary" fontFamily="monospace">
            {new Date(log.created_at).toLocaleString("it-IT")}
          </Typography>
        </Stack>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Divider />

      <Section title="Identificazione">
        <Field label="Log ID" value={log.id} mono />
        <Field label="Request ID" value={log.request_id} mono />
        {log.lead_id && <Field label="Lead ID" value={log.lead_id} mono />}
        {log.external_ref && (
          <Field label="External ref" value={log.external_ref} mono />
        )}
        {log.partner_id && (
          <Field label="Partner ID" value={log.partner_id} mono />
        )}
        {log.api_key_id && (
          <Field label="API key ID" value={log.api_key_id} mono />
        )}
        <Field
          label="Scadenza"
          value={`${new Date(log.expires_at).toLocaleString("it-IT")} (TTL 90gg)`}
        />
      </Section>

      <Section title="Dispatch">
        <Field label="Rule" value={log.rule_id} mono />
        <Field label="Rule type" value={log.rule_type} />
        {log.dispatcher_rule_id && (
          <Field label="Dispatcher" value={log.dispatcher_rule_id} mono />
        )}
        {log.simple_rule_id && (
          <Field label="Simple" value={log.simple_rule_id} mono />
        )}
        {log.destination_url && (
          <Field label="URL" value={log.destination_url} mono />
        )}
        {log.destination_method && (
          <Field label="Method" value={log.destination_method} mono />
        )}
        {typeof log.duration_ms === "number" && (
          <Field label="Durata" value={`${log.duration_ms} ms`} />
        )}
        {typeof log.attempt === "number" && (
          <Field label="Attempt" value={`${log.attempt} / 3`} />
        )}
      </Section>

      {log.http_call && (
        <Section title="HTTP Call">
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Request headers
            </Typography>
            <Box
              sx={{
                mt: 0.5,
                p: 1,
                bgcolor: "grey.100",
                borderRadius: 1,
                fontFamily: "monospace",
                fontSize: 11,
              }}
            >
              {Object.entries(log.http_call.request.headers ?? {}).length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  Nessun header
                </Typography>
              )}
              {Object.entries(log.http_call.request.headers ?? {}).map(
                ([k, v]) => (
                  <Box key={k} sx={{ wordBreak: "break-all" }}>
                    <strong>{k}:</strong>{" "}
                    {isSensitiveHeader(k) ? "[REDACTED]" : v}
                  </Box>
                )
              )}
            </Box>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            Request body
          </Typography>
          <Pre>{log.http_call.request.body || "(empty)"}</Pre>

          {log.http_call.response && (
            <>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Chip
                  label={`HTTP ${log.http_call.response.status_code}`}
                  size="small"
                  variant="outlined"
                  color={
                    log.http_call.response.status_code >= 400
                      ? "error"
                      : "success"
                  }
                  sx={{ fontFamily: "monospace" }}
                />
                <Chip
                  label={`${log.http_call.response.latency_ms} ms`}
                  size="small"
                  variant="outlined"
                  sx={{ fontFamily: "monospace" }}
                />
                {log.http_call.response.truncated && (
                  <Chip label="truncated" size="small" color="warning" />
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Response body
              </Typography>
              <Pre>{log.http_call.response.body || "(empty)"}</Pre>
            </>
          )}

          {log.http_call.error && (
            <Box
              sx={{
                mt: 1,
                p: 1.5,
                bgcolor: "error.50",
                color: "error.main",
                border: "1px solid",
                borderColor: "error.light",
                borderRadius: 1,
                fontFamily: "monospace",
                fontSize: 12,
                whiteSpace: "pre-wrap",
              }}
            >
              {log.http_call.error}
            </Box>
          )}
        </Section>
      )}

      {(log.status === "error" || log.status === "failed") &&
        (log.error_code || log.error_message) && (
          <Section title="Errore">
            {log.error_code && <Field label="Code" value={log.error_code} mono />}
            {log.error_message && (
              <Box
                sx={{
                  mt: 1,
                  p: 1.5,
                  bgcolor: "error.50",
                  color: "error.main",
                  border: "1px solid",
                  borderColor: "error.light",
                  borderRadius: 1,
                  fontFamily: "monospace",
                  fontSize: 12,
                  whiteSpace: "pre-wrap",
                }}
              >
                {log.error_message}
              </Box>
            )}
          </Section>
        )}

      {log.payload_snapshot && Object.keys(log.payload_snapshot).length > 0 && (
        <Section title="Payload snapshot">
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            PII redatta lato server (email/phone/CF → [REDACTED]).
          </Typography>
          <Pre>{JSON.stringify(log.payload_snapshot, null, 2)}</Pre>
        </Section>
      )}

      {log.response_snapshot?.body && (
        <Section title="Response snapshot">
          <Pre>{log.response_snapshot.body}</Pre>
        </Section>
      )}
    </Box>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Box>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ display: "block", mb: 0.5 }}
      >
        {title}
      </Typography>
      <Stack spacing={0.5}>{children}</Stack>
    </Box>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value?: string;
  mono?: boolean;
}) {
  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "baseline" }}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 100 }}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontFamily={mono ? "monospace" : undefined}
        sx={{ wordBreak: "break-all" }}
      >
        {value ?? "—"}
      </Typography>
    </Box>
  );
}

function Pre({ children }: { children: React.ReactNode }) {
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        mt: 0.5,
        p: 1.5,
        bgcolor: "grey.100",
        fontSize: 11.5,
        fontFamily: "monospace",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        maxHeight: 280,
        overflow: "auto",
        border: "1px solid",
        borderColor: "grey.300",
        borderRadius: 1,
      }}
    >
      {children}
    </Box>
  );
}
